import { useEffect, useState } from "react";
import Modal from "@modalSrc/Modal";
import RightTimeApi from "@/API";
import { toast } from "react-toastify";
import { downloadResponseAsFile } from "@/utils/authenticatedFileDownload";

type Format50ReportModalProps = {
  isOpen: boolean;
  onClose: () => void;
  updateSpinner?: (show: boolean) => void;
};

type CompanyOption = { id: number; name?: string; alias?: string; active?: boolean };

type TestTypeOption = { companyTestTypeId: number; name: string; testCount: number };

type SectionOption = {
  sectionId: number;
  formId: number;
  name: string;
  companyTestTypeIds: number[];
};

type OptionsResponse = {
  companyTestTypes: TestTypeOption[];
  sections: SectionOption[];
};

const STEP_CRITERIA = 1;
const STEP_TEST_TYPES = 2;
const STEP_SECTIONS = 3;

export default function Format50ReportModal({ isOpen, onClose, updateSpinner }: Format50ReportModalProps) {
  const [step, setStep] = useState<number>(STEP_CRITERIA);
  const [companies, setCompanies] = useState<CompanyOption[]>([]);
  const [companyId, setCompanyId] = useState<number | null>(null);
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");

  const [testTypes, setTestTypes] = useState<TestTypeOption[]>([]);
  const [sections, setSections] = useState<SectionOption[]>([]);
  const [selectedCttIds, setSelectedCttIds] = useState<number[]>([]);
  const [selectedSectionIds, setSelectedSectionIds] = useState<number[]>([]);
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    if (isOpen) {
      resetState();
      loadCompaniesAsync();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  function resetState() {
    setStep(STEP_CRITERIA);
    setCompanyId(null);
    setStartDate("");
    setEndDate("");
    setTestTypes([]);
    setSections([]);
    setSelectedCttIds([]);
    setSelectedSectionIds([]);
  }

  async function loadCompaniesAsync() {
    updateSpinner?.(true);
    try {
      const resp = await RightTimeApi.getAsync("/CompaniesList");
      if (resp && resp.ok && Array.isArray(resp.jsonData)) {
        const active = resp.jsonData.filter((c: CompanyOption) => c.active !== false);
        active.sort((a: CompanyOption, b: CompanyOption) =>
          (a.alias || a.name || "").localeCompare(b.alias || b.name || ""));
        setCompanies(active);
      } else {
        toast.error("Failed to load companies.");
      }
    } catch {
      toast.error("Error loading companies.");
    } finally {
      updateSpinner?.(false);
    }
  }

  async function loadOptionsAsync() {
    if (!companyId) {
      toast.error("Please select a company.");
      return;
    }
    if (!startDate || !endDate) {
      toast.error("Please provide a start and end date.");
      return;
    }
    if (endDate < startDate) {
      toast.error("End date must be on or after start date.");
      return;
    }

    updateSpinner?.(true);
    setLoading(true);
    try {
      const url = `/SystemReport/Format50/Options?companyId=${companyId}&startDate=${startDate}&endDate=${endDate}`;
      const resp = await RightTimeApi.getAsync(url);
      if (resp && resp.ok && resp.jsonData) {
        const data = resp.jsonData as OptionsResponse;
        const ctts = data.companyTestTypes ?? [];
        const secs = data.sections ?? [];
        if (ctts.length === 0) {
          toast.info("No completed tests found for that company and date range.");
          return;
        }
        setTestTypes(ctts);
        setSections(secs);
        setSelectedCttIds(ctts.map((t) => t.companyTestTypeId));
        setSelectedSectionIds(secs.map((s) => s.sectionId));
        setStep(STEP_TEST_TYPES);
      } else {
        toast.error("Failed to load report options.");
      }
    } catch {
      toast.error("Error loading report options.");
    } finally {
      setLoading(false);
      updateSpinner?.(false);
    }
  }

  function visibleSections(): SectionOption[] {
    return sections.filter((s) => s.companyTestTypeIds.some((id) => selectedCttIds.includes(id)));
  }

  function goToSections() {
    if (selectedCttIds.length === 0) {
      toast.error("Please select at least one test type.");
      return;
    }
    const visibleIds = visibleSections().map((s) => s.sectionId);
    setSelectedSectionIds(visibleIds);
    setStep(STEP_SECTIONS);
  }

  function toggleCtt(id: number) {
    setSelectedCttIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function toggleSection(id: number) {
    setSelectedSectionIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  async function downloadAsync() {
    const visibleIds = visibleSections().map((s) => s.sectionId);
    const sectionIds = selectedSectionIds.filter((id) => visibleIds.includes(id));
    if (sectionIds.length === 0) {
      toast.error("Please select at least one section.");
      return;
    }

    updateSpinner?.(true);
    setLoading(true);
    try {
      const body = {
        companyId,
        startDate,
        endDate,
        companyTestTypeIds: selectedCttIds,
        sectionIds,
      };
      const resp = await RightTimeApi.postAsync("/SystemReport/Format50/Download", body);
      const ok = await downloadResponseAsFile(
        resp,
        `Format50-${companyId}-${startDate}-${endDate}.tsv`
      );
      if (!ok) {
        toast.error("Failed to generate report.");
        return;
      }
      toast.success("Report downloaded.");
      onClose();
    } catch {
      toast.error("Error generating report.");
    } finally {
      setLoading(false);
      updateSpinner?.(false);
    }
  }

  function renderFooter() {
    if (step === STEP_CRITERIA) {
      return (
        <div className="footerRight">
          <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button type="button" className="btn btn-primary" onClick={loadOptionsAsync} disabled={loading}>Next</button>
        </div>
      );
    }
    if (step === STEP_TEST_TYPES) {
      return (
        <div className="footerRight">
          <button type="button" className="btn btn-secondary" onClick={() => setStep(STEP_CRITERIA)}>Back</button>
          <button type="button" className="btn btn-primary" onClick={goToSections} disabled={selectedCttIds.length === 0}>Next</button>
        </div>
      );
    }
    return (
      <div className="footerRight">
        <button type="button" className="btn btn-secondary" onClick={() => setStep(STEP_TEST_TYPES)}>Back</button>
        <button type="button" className="btn btn-primary" onClick={downloadAsync} disabled={loading}>Download</button>
      </div>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Completed Test Data (Format 50)"
      size="md"
      className="format50Modal"
      footer={renderFooter()}
    >
      <div className="format50Body">
        <ol className="wizardSteps">
          <li className={step === STEP_CRITERIA ? "active" : ""}>Company &amp; Dates</li>
          <li className={step === STEP_TEST_TYPES ? "active" : ""}>Test Types</li>
          <li className={step === STEP_SECTIONS ? "active" : ""}>Sections</li>
        </ol>

        {step === STEP_CRITERIA && (
          <div className="wizardPanel">
            <label className="fieldRow">
              <span className="fieldLabel">Company</span>
              <select
                value={companyId ?? ""}
                onChange={(e) => setCompanyId(e.target.value ? parseInt(e.target.value, 10) : null)}
              >
                <option value="">Select a company...</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.alias || c.name}</option>
                ))}
              </select>
            </label>
            <label className="fieldRow">
              <span className="fieldLabel">Start date</span>
              <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            </label>
            <label className="fieldRow">
              <span className="fieldLabel">End date</span>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
            </label>
          </div>
        )}

        {step === STEP_TEST_TYPES && (
          <div className="wizardPanel">
            <p className="wizardHint">Select the test types to include.</p>
            <ul className="checklist">
              {testTypes.map((t) => (
                <li key={t.companyTestTypeId}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedCttIds.includes(t.companyTestTypeId)}
                      onChange={() => toggleCtt(t.companyTestTypeId)}
                    />
                    <span className="checklistLabel">{t.name}</span>
                    <span className="checklistMeta">{t.testCount} test{t.testCount === 1 ? "" : "s"}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}

        {step === STEP_SECTIONS && (
          <div className="wizardPanel">
            <p className="wizardHint">All sections are selected by default. Deselect any you want to exclude.</p>
            <ul className="checklist">
              {visibleSections().map((s) => (
                <li key={s.sectionId}>
                  <label>
                    <input
                      type="checkbox"
                      checked={selectedSectionIds.includes(s.sectionId)}
                      onChange={() => toggleSection(s.sectionId)}
                    />
                    <span className="checklistLabel">{s.name}</span>
                    <span className="checklistMeta">Form {s.formId}</span>
                  </label>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Modal>
  );
}
